import type { Monitor, Recipient } from '../types/monitor'
import { daysFromNow, hoursFromNow, minutesFromNow } from '../utils/datetime'

/** Deterministic pseudo-random series so charts look alive but never flicker. */
function series(count: number, base: number, spread: number, seed: number) {
  const points: number[] = []
  let value = seed
  for (let index = 0; index < count; index += 1) {
    value = (value * 9301 + 49297) % 233280
    const noise = (value / 233280 - 0.5) * spread
    points.push(Math.max(24, Math.round(base + noise)))
  }
  return points
}

export const recipients: Recipient[] = [
  { id: 'r1', email: 'priya@acme.com', verified: true },
  { id: 'r2', email: 'ops@acme.com', verified: true },
  { id: 'r3', email: 'finance@acme.com', verified: false },
]

export const monitors: Monitor[] = [
  {
    id: 'm1',
    domain: 'acme.com',
    label: 'Marketing site',
    sslEnabled: true,
    uptimeEnabled: true,
    certificate: {
      subject: 'CN=acme.com',
      issuer: "Let's Encrypt R11",
      validFrom: daysFromNow(-32),
      validTo: daysFromNow(58),
      serial: '04:9F:2C:1A:D8:5B:77:E0:3C:91',
      algorithm: 'ECDSA P-256, SHA-256',
      chainValid: true,
      altNames: ['acme.com', 'www.acme.com'],
    },
    certificateError: null,
    warningWindowDays: 21,
    reminderCadence: [30, 14, 7, 1],
    pingIntervalMinutes: 1,
    method: 'GET',
    expectedStatus: 200,
    timeoutSeconds: 10,
    failureThreshold: 2,
    isUp: true,
    responseTimes: series(48, 182, 70, 7),
    availability24h: 100,
    availability7d: 99.98,
    availability30d: 99.94,
    incidents: [
      {
        id: 'i1',
        startedAt: daysFromNow(-11, 3),
        endedAt: daysFromNow(-11, 3),
        durationMinutes: 6,
        statusCode: 502,
        reason: 'Bad gateway during origin deploy',
      },
    ],
    lastCheckedAt: minutesFromNow(-1),
    nextCheckAt: minutesFromNow(1),
    recipientIds: ['r1', 'r2'],
  },
  {
    id: 'm2',
    domain: 'shop.acme.com',
    label: 'Storefront',
    sslEnabled: true,
    uptimeEnabled: true,
    certificate: {
      subject: 'CN=shop.acme.com',
      issuer: 'DigiCert TLS RSA SHA256 2020 CA1',
      validFrom: daysFromNow(-356),
      validTo: daysFromNow(9),
      serial: '0A:33:F1:90:44:2B:6C:1E:77:B2',
      algorithm: 'RSA 2048, SHA-256',
      chainValid: true,
      altNames: ['shop.acme.com'],
    },
    certificateError: null,
    warningWindowDays: 30,
    reminderCadence: [30, 14, 7, 3, 1],
    pingIntervalMinutes: 1,
    method: 'GET',
    expectedStatus: 200,
    timeoutSeconds: 10,
    failureThreshold: 2,
    isUp: true,
    responseTimes: series(48, 265, 120, 19),
    availability24h: 99.97,
    availability7d: 99.91,
    availability30d: 99.86,
    incidents: [
      {
        id: 'i2',
        startedAt: daysFromNow(-4, 22),
        endedAt: daysFromNow(-4, 22),
        durationMinutes: 14,
        statusCode: 503,
        reason: 'Checkout service unavailable',
      },
      {
        id: 'i3',
        startedAt: daysFromNow(-19, 14),
        endedAt: daysFromNow(-19, 15),
        durationMinutes: 41,
        statusCode: null,
        reason: 'Connection timed out after 10s',
      },
    ],
    lastCheckedAt: minutesFromNow(-1),
    nextCheckAt: minutesFromNow(1),
    recipientIds: ['r1', 'r2'],
  },
  {
    id: 'm3',
    domain: 'api.acme.com',
    label: 'Public API',
    sslEnabled: true,
    uptimeEnabled: true,
    certificate: {
      subject: 'CN=api.acme.com',
      issuer: "Let's Encrypt R11",
      validFrom: daysFromNow(-14),
      validTo: daysFromNow(76),
      serial: '03:BE:71:5C:29:AA:40:D1:88:6F',
      algorithm: 'ECDSA P-256, SHA-256',
      chainValid: true,
      altNames: ['api.acme.com', 'api-eu.acme.com'],
    },
    certificateError: null,
    warningWindowDays: 21,
    reminderCadence: [30, 14, 7, 1],
    pingIntervalMinutes: 1,
    method: 'GET',
    expectedStatus: 200,
    timeoutSeconds: 8,
    failureThreshold: 3,
    isUp: false,
    responseTimes: [
      ...series(40, 120, 40, 31),
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
    ],
    availability24h: 96.4,
    availability7d: 99.31,
    availability30d: 99.58,
    incidents: [
      {
        id: 'i4',
        startedAt: minutesFromNow(-52),
        endedAt: null,
        durationMinutes: null,
        statusCode: 500,
        reason: 'Internal server error on /v1/health',
      },
      {
        id: 'i5',
        startedAt: daysFromNow(-6, 9),
        endedAt: daysFromNow(-6, 9),
        durationMinutes: 22,
        statusCode: 504,
        reason: 'Gateway timeout',
      },
    ],
    lastCheckedAt: minutesFromNow(-1),
    nextCheckAt: minutesFromNow(1),
    recipientIds: ['r1', 'r2'],
  },
  {
    id: 'm4',
    domain: 'blog.acme.com',
    label: 'Editorial blog',
    sslEnabled: true,
    uptimeEnabled: false,
    certificate: {
      subject: 'CN=blog.acme.com',
      issuer: 'Google Trust Services WE1',
      validFrom: daysFromNow(-3),
      validTo: daysFromNow(87),
      serial: '7F:12:08:C4:5D:31:9A:2B:EE:04',
      algorithm: 'ECDSA P-256, SHA-256',
      chainValid: true,
      altNames: ['blog.acme.com'],
    },
    certificateError: null,
    warningWindowDays: 21,
    reminderCadence: [30, 14, 7],
    pingIntervalMinutes: 5,
    method: 'GET',
    expectedStatus: 200,
    timeoutSeconds: 10,
    failureThreshold: 2,
    isUp: true,
    responseTimes: series(48, 210, 60, 53),
    availability24h: 100,
    availability7d: 100,
    availability30d: 99.99,
    incidents: [],
    lastCheckedAt: hoursFromNow(-6),
    nextCheckAt: hoursFromNow(18),
    recipientIds: ['r1'],
  },
  {
    id: 'm5',
    domain: 'status.acme.com',
    label: 'Status page',
    sslEnabled: false,
    uptimeEnabled: true,
    certificate: null,
    certificateError: null,
    warningWindowDays: 21,
    reminderCadence: [30, 14, 7],
    pingIntervalMinutes: 5,
    method: 'HEAD',
    expectedStatus: 200,
    timeoutSeconds: 10,
    failureThreshold: 2,
    isUp: true,
    responseTimes: series(48, 340, 220, 71),
    availability24h: 98.9,
    availability7d: 99.42,
    availability30d: 99.61,
    incidents: [
      {
        id: 'i6',
        startedAt: hoursFromNow(-9),
        endedAt: hoursFromNow(-8),
        durationMinutes: 16,
        statusCode: 429,
        reason: 'Rate limited by CDN edge',
      },
    ],
    lastCheckedAt: minutesFromNow(-3),
    nextCheckAt: minutesFromNow(2),
    recipientIds: ['r2'],
  },
  {
    id: 'm6',
    domain: 'legacy.acme.io',
    label: 'Legacy portal',
    sslEnabled: true,
    uptimeEnabled: false,
    certificate: null,
    certificateError:
      'Handshake failed — the server closed the connection before presenting a certificate.',
    warningWindowDays: 21,
    reminderCadence: [30, 14, 7],
    pingIntervalMinutes: 30,
    method: 'GET',
    expectedStatus: 200,
    timeoutSeconds: 10,
    failureThreshold: 2,
    isUp: true,
    responseTimes: series(48, 520, 180, 97),
    availability24h: 100,
    availability7d: 99.8,
    availability30d: 99.7,
    incidents: [],
    lastCheckedAt: hoursFromNow(-6),
    nextCheckAt: hoursFromNow(18),
    recipientIds: ['r2'],
  },
]
